import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '@center-pwa/store/store';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import { Card } from 'primereact/card';
import SphereAvatar from '../../molecules/sphere-avatar/sphere-avatar';
import { GlyphColor } from '../../../types/glyph';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { FALLBACK_METASPACE_SYSTEM_NAME } from '../../../constants/metaspaces';
import './ThreadContext.scss';

interface ThreadContextProps {
  contextKey: string; // "sphere:{sphereId}" or "sphere:{sphereId}|metaspace:{metaspaceId}"
  className?: string;
}

const ThreadContext: React.FC<ThreadContextProps> = ({ contextKey, className = '' }) => {
  // Get API clients
  const apiClients = useApiClientsOrNull();
  console.log('🧵 [ThreadContext] Component render', {
    hasApiClients: !!apiClients,
    hasGraphClient: !!apiClients?.graphClient,
    contextKey
  });

  // State for fetched sphere data - keyed by sphereId to avoid conflicts
  const [fetchedSphereData, setFetchedSphereData] = useState<{
    sphereId: string;
    name: string;
    glyph?: string;
    glyphColor?: GlyphColor;
    imageId?: string;
  } | null>(null);
  const [isLoadingSphere, setIsLoadingSphere] = useState(false);

  // Get systemName from URL params (metaspace routes now use system_name)
  const { systemName } = useParams<{ sphereId: string; systemName: string }>();

  // Extract sphereId and metaspaceId from contextKey
  const { sphereId: contextSphereId, metaspaceId: contextMetaspaceId } = useMemo(() => {
    // Handle format: "sphere:{sphereId}|metaspace:{metaspaceId}" or "sphere:{sphereId}"
    const sphereMatch = contextKey.match(/sphere:([^|]+)/);
    const metaspaceMatch = contextKey.match(/metaspace:([^|]+)/);

    return {
      sphereId: sphereMatch ? sphereMatch[1] : '',
      metaspaceId: metaspaceMatch ? metaspaceMatch[1] : undefined
    };
  }, [contextKey]);

  // Use contextSphereId as the primary sphereId
  const sphereId = contextSphereId;

  const metaspaces = useSelector(selectAllMetaspaces);

  // Get the last event from the event system to check for Atlas context
  const lastEvent = useSelector((state: RootState) => state.metaspaceEvents?.lastEvent);

  // Fetch sphere information if not available from events
  useEffect(() => {
    const fetchSphereInfo = async () => {
      console.log('🧵 [ThreadContext] useEffect triggered', {
        hasApiClient: !!apiClients?.graphClient,
        sphereId,
        fetchedSphereData,
        isLoadingSphere
      });

      // Early returns to prevent unnecessary calls
      if (!apiClients?.graphClient || !sphereId) {
        console.log('🧵 [ThreadContext] Early return: no API client or sphereId');
        return;
      }

      // Don't fetch if we already have the data for this specific sphere or are currently loading
      const hasDataForThisSphere = fetchedSphereData?.sphereId === sphereId;
      if (hasDataForThisSphere || isLoadingSphere) {
        console.log('🧵 [ThreadContext] Early return: already have data for this sphere or loading', {
          hasDataForThisSphere,
          fetchedSphereId: fetchedSphereData?.sphereId,
          currentSphereId: sphereId,
          isLoadingSphere
        });
        return;
      }

      // Check if we have complete sphere info from the last event for this specific sphere
      const isEventForThisSphere = lastEvent?.type === 'CHAT_TRIGGERED' && lastEvent?.payload?.sphereId === sphereId;
      const hasCompleteEventData = isEventForThisSphere && lastEvent.payload.sphereName && lastEvent.payload.sphereAvatar && lastEvent.payload.sphereImageId;

      if (hasCompleteEventData) {
        console.log('🧵 [ThreadContext] Using complete sphere info from event, skipping API call');
        return;
      }

      // Always make API call if we don't have fetched data and event data is incomplete
      console.log('🧵 [ThreadContext] Event data incomplete or missing, proceeding with API call:', {
        hasEvent: isEventForThisSphere,
        hasName: !!lastEvent?.payload?.sphereName,
        hasAvatar: !!lastEvent?.payload?.sphereAvatar,
        hasImageId: !!lastEvent?.payload?.sphereImageId
      });

      console.log('🧵 [ThreadContext] Starting sphere fetch for:', sphereId);
      setIsLoadingSphere(true);

      try {
        const response = await apiClients.graphClient.get(`/spheres/${sphereId}`);
        console.log('🧵 [ThreadContext] API response received for:', sphereId);

        // Extract sphere data from the response
        if (response?.sphere?.properties?.name) {
          const sphereData = {
            sphereId,
            name: response.sphere.properties.name,
            glyph: response.sphere.glyph,
            glyphColor: response.sphere.glyphColor,
            imageId: response.sphere.properties.imageId
          };
          console.log('🧵 [ThreadContext] Setting sphere data:', sphereData);
          setFetchedSphereData(sphereData);
        } else {
          console.log('🧵 [ThreadContext] No sphere name found in response');
          setFetchedSphereData({ sphereId, name: 'Unknown Sphere' }); // Set a fallback to prevent re-fetching
        }
      } catch (error: any) {
        console.error('🧵 [ThreadContext] Error fetching sphere:', error);
        setFetchedSphereData({ sphereId, name: 'Error Loading Sphere' }); // Set fallback to prevent re-fetching
      } finally {
        setIsLoadingSphere(false);
      }
    };

    fetchSphereInfo();
  }, [apiClients?.graphClient, sphereId]); // Only re-run when API client or sphereId changes

  // Reset fetched sphere data when sphereId changes
  useEffect(() => {
    console.log('🧵 [ThreadContext] sphereId changed, resetting state:', sphereId);
    setFetchedSphereData(null);
    setIsLoadingSphere(false);
  }, [sphereId]);

  // Find the current metaspace by system name or context ID
  const currentMetaspaceInfo = useMemo(() => {
    // Prioritize contextMetaspaceId, then URL systemName
    const effectiveSystemName = systemName;
    const effectiveMetaspaceId = contextMetaspaceId;

    console.log('🧵 [ThreadContext] Debug metaspace data:', {
      metaspaces,
      systemName,
      contextMetaspaceId,
      effectiveSystemName,
      effectiveMetaspaceId,
      metaspacesLength: metaspaces.length
    });

    // Try to find metaspace by system_name (from URL) or by ID (from context)
    if (metaspaces.length > 0) {
      let foundMetaspace;
      if (effectiveSystemName) {
        foundMetaspace = metaspaces.find((m: any) => m.system_name === effectiveSystemName);
      }
      if (!foundMetaspace && effectiveMetaspaceId) {
        foundMetaspace = metaspaces.find((m: any) => m.metaspace_id === effectiveMetaspaceId);
      }
      if (foundMetaspace) {
        console.log('🧵 [ThreadContext] Found metaspace:', foundMetaspace);
        return { name: foundMetaspace.name, id: foundMetaspace.metaspace_id, description: foundMetaspace.description };
      }
    }

    // Default metaspace names based on common system names
    if (effectiveSystemName === FALLBACK_METASPACE_SYSTEM_NAME) {
      console.log('🧵 [ThreadContext] Using Armillary View metaspace');
      return { name: 'Armillary View', id: effectiveMetaspaceId };
    }

    const fallback = { name: `Metaspace ${effectiveSystemName || effectiveMetaspaceId?.slice(0, 8) || 'Current'}`, id: effectiveMetaspaceId };
    console.log('🧵 [ThreadContext] Using fallback metaspace:', fallback);
    return fallback;
  }, [metaspaces, systemName, contextMetaspaceId]);

  // Create context info from available data
  const contextInfo = useMemo(() => {
    if (!sphereId) return null;

    // Check if the last event is relevant to this sphere
    const isEventForThisSphere = lastEvent?.type === 'CHAT_TRIGGERED' && lastEvent?.payload?.sphereId === sphereId;

    // Determine the best sphere name to display
    let displaySphereName: string | undefined;
    let sphereGlyph: string | undefined;
    let sphereGlyphColor: GlyphColor | undefined;
    let sphereImageId: string | undefined;

    if (isEventForThisSphere && lastEvent.payload.sphereName) {
      // Use sphere name from Atlas event
      displaySphereName = lastEvent.payload.sphereName;
      sphereGlyph = lastEvent.payload.sphereAvatar;
      sphereImageId = lastEvent.payload.sphereImageId;
      console.log('🧵 [ThreadContext] Using sphere data from event:', { displaySphereName, sphereGlyph, sphereImageId });
    }

    // If we have fetched data, use it to fill in any missing information
    if (fetchedSphereData) {
      displaySphereName = displaySphereName || fetchedSphereData.name;
      sphereGlyph = sphereGlyph || fetchedSphereData.glyph;
      sphereGlyphColor = sphereGlyphColor || fetchedSphereData.glyphColor;
      sphereImageId = sphereImageId || fetchedSphereData.imageId;
      console.log('🧵 [ThreadContext] Using combined sphere data:', { displaySphereName, sphereGlyph, sphereImageId });
    }

    // Handle loading and fallback states
    if (!displaySphereName) {
      if (isLoadingSphere) {
        displaySphereName = 'Loading sphere...';
        console.log('🧵 [ThreadContext] Showing loading state');
      } else {
        displaySphereName = `Sphere ${sphereId.slice(0, 8)}...`;
        console.log('🧵 [ThreadContext] Using fallback sphere name:', displaySphereName);
      }
    }

    const result = {
      sphereId,
      sphereName: displaySphereName || `Sphere ${sphereId.slice(0, 8)}...`,
      sphereGlyph,
      sphereGlyphColor,
      sphereImageId,
      metaspaceName: currentMetaspaceInfo.name,
      metaspaceId: currentMetaspaceInfo.id,
      triggerSource: isEventForThisSphere ? lastEvent.payload.triggerSource : undefined,
      level: isEventForThisSphere ? lastEvent.payload.level : undefined,
      parentSphereId: isEventForThisSphere ? lastEvent.payload.parentSphereId : undefined,
    };

    console.log('🧵 [ThreadContext] Final context info:', result);
    return result;
  }, [sphereId, currentMetaspaceInfo, lastEvent, fetchedSphereData, isLoadingSphere]);

  if (!contextInfo) {
    return null;
  }

  const displayName = contextInfo.sphereName || `Sphere ${contextInfo.sphereId.slice(0, 8)}...`;
  const metaspaceName = contextInfo.metaspaceName || 'Current Metaspace';

  return (
    <div className={`thread-context ${className}`}>
      <Card className="thread-context-card">
        <div className="thread-context-details">
                    <div className="thread-context-icon">
            <i className="pi pi-link" />
          </div>
          <div className="thread-context-info">
            <div className="thread-context-title">Thread Context</div>            
          </div>
          <div className="context-item">
            <SphereAvatar
              sphereId={contextInfo.sphereId}
              glyph={contextInfo.sphereGlyph}
              glyphColor={contextInfo.sphereGlyphColor}
              imageId={contextInfo.sphereImageId}
              size="small"
              className="context-avatar mb-1"
            />
            <div className="context-content">
              <div className="context-label">Sphere</div>
              <div className="context-value" title={displayName}>
                {displayName}
              </div>
            </div>
          </div>

          <div className="context-item">
            <div className="context-icon">
              <i className="metaspace-icon" />
            </div>
            <div className="context-content">
              <div className="context-label">Metaspace</div>
              <div className="context-value" title={metaspaceName}>
                {metaspaceName}
              </div>
            </div>
          </div>

        </div>
      </Card>
    </div>
  );
};

export default ThreadContext;